import { useEffect, useMemo, useRef } from 'react';
import { CircleNotch } from '@phosphor-icons/react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import ROUTES from '@/shared/constants/routes';
import { getIsNative } from '@/shared/utils/general.util';
import { useInvoiceV2Tracking } from './hooks/useInvoiceV2Tracking';
import { MY_INVOICES_LANDING } from './v2/lib/myInvoicesNavigation';

const RETRY_AFTER_MS = 4_000;
const HARD_REDIRECT_AFTER_MS = 10_000;

/**
 * `/invoice/*` ya no sirve el flujo legacy: la facturación vive en `/facturacion`.
 *
 * Queda para las entradas viejas (un marcador, una pestaña abierta, la liga de un
 * correo, el sello de declaraciones) y para el tab «Facturación» de la app nativa,
 * que todavía abre `/invoice/` en los binarios instalados. El detalle de una factura
 * cae en «Mis facturas»: su id es el del NestJS y la v2 abre por periodo y UUID.
 * Quien pidió su QR con `openMyQr` (solo lo manda el banner del home) llega a «Mi
 * página de facturación» con su origen.
 *
 * El spinner cubre el tiempo en que el router deja montada ESTA ruta mientras baja
 * los chunks perezosos de `/facturacion`.
 *
 * Por qué no `<Navigate>`: su efecto corre una sola vez por montaje. Si la
 * navegación pendiente a `/facturacion` se interrumpe y el router regresa a
 * `/invoice/` sin desmontar esta ruta, `<Navigate>` no vuelve a disparar y el
 * spinner se queda para siempre (pasó en la app de iOS el 3-oct: había que ir a
 * Inicio y regresar). Aquí el salto se pide en cada ubicación en la que aterriza la
 * ruta (`location` es un objeto nuevo por cada navegación completada), y un vigía lo
 * vuelve a pedir a los 4 s y recarga directo a los 10 s: el spinner nunca se queda
 * para siempre. Cada vez que el vigía actúa queda `invoice_v2_legacy_redirect_stuck`.
 * El tracker va en un ref para que un cambio de identidad no vuelva a disparar el
 * efecto y aborte la navegación en curso.
 */
export const LegacyInvoicingRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { trackLegacyRedirectStuck } = useInvoiceV2Tracking();
  const trackStuck = useRef(trackLegacyRedirectStuck);
  trackStuck.current = trackLegacyRedirectStuck;
  const isInvoiceDetail = useMatch(ROUTES.INVOICE_DETAIL) !== null;
  const wantsMyQr = Boolean((location.state as { openMyQr?: boolean } | null)?.openMyQr);

  const target = wantsMyQr ? ROUTES.FACTURACION_QR : ROUTES.FACTURACION;
  const targetState = useMemo(() => {
    if (wantsMyQr) return { qrEntry: 'home_banner' };
    return isInvoiceDetail ? MY_INVOICES_LANDING : undefined;
  }, [wantsMyQr, isInvoiceDetail]);

  useEffect(() => {
    void navigate(target, { replace: true, state: targetState });

    const startedAt = performance.now();
    const report = (stage: 'renavigate' | 'hard_redirect') =>
      trackStuck.current({
        stage,
        elapsedMs: Math.round(performance.now() - startedAt),
        target,
        isNative: getIsNative(),
        online: navigator.onLine,
      });

    const retry = window.setTimeout(() => {
      report('renavigate');
      void navigate(target, { replace: true, state: targetState });
    }, RETRY_AFTER_MS);
    const hard = window.setTimeout(() => {
      report('hard_redirect');
      window.location.replace(target);
    }, HARD_REDIRECT_AFTER_MS);

    return () => {
      window.clearTimeout(retry);
      window.clearTimeout(hard);
    };
  }, [location, navigate, target, targetState]);

  return (
    <div role='status' aria-label='Abriendo facturación' className='flex min-h-[60dvh] items-center justify-center'>
      <CircleNotch className='text-h-primary-50 animate-spin motion-reduce:animate-none' size={32} weight='bold' />
    </div>
  );
};
